/* eslint-disable camelcase */
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import Select from 'react-select';
import { executeHttpRequest } from '../../epics/api';
import { TClientPayGrade, TPageNotification, TServiceKey } from '../../types';
import { getErrorMessages } from '../../utils';
import { TRootState } from '../../reducers';
import { ModalConfirmationWithErrorAndLoader } from '../common/ModalConfirmationWithErrorAndLoader';

type TAddPayGradeFields = {
    service: TServiceKey;
    role: string;
    skill: string;
    freelancer_pay_per_hour: number | string;
    client_pay_per_hour: number | string;
}

type TEditPayGradeFields = {
    client_pay_per_hour: number | string;
    freelancer_pay_per_hour: number | string;
}

type TClientPayGradesProps = {
    clientId: string;
    notification: TPageNotification;
}

export default function ClientPayGrades({
    clientId,
    notification,
}: TClientPayGradesProps) {
    const [payGrades, setAddresses] = useState<TClientPayGrade[]>([]);

    const { servicesStructure } = useSelector((state: TRootState) => state);

    // Add
    const initialAddFields: TAddPayGradeFields = {
        service: null,
        role: null,
        skill: null,
        freelancer_pay_per_hour: '',
        client_pay_per_hour: '',
    };
    const [addFields, setAddFields] = useState<TAddPayGradeFields>(initialAddFields);
    const [isAdding, setIsAdding] = useState<boolean>(false);
    const [serviceOptions, setServiceOptions] = useState([]);
    const [roleOptions, setRoleOptions] = useState([]);
    const [skillOptions, setSkillOptions] = useState([]);

    // Edit
    const [editingId, setEditingId] = useState<number | null>(null);
    const [editingError, setEditingError] = useState<string | null>(null);
    const [editFields, setEditFields] = useState<TEditPayGradeFields | null>(null);
    const [isEditing, setIsEditing] = useState<boolean>(false);

    // Delete
    const [deletingPayGrade, setDeletingPayGrade] = useState<TClientPayGrade | null>(null);
    const [deletingError, setDeletingError] = useState<string | null>(null);
    const [isDeleting, setIsDeleting] = useState<boolean>(false);

    const fetchPayGrades = () =>
        executeHttpRequest(`clients/${clientId}/paygrades/`)
            .then(response => {
                setAddresses(response.data.content);
            })
            .catch(err => {
                notification.error(
                    'Error',
                    `Could not fetch client pay grades: ${getErrorMessages(err)}`,
                );
            });

    const getServiceOptions = () =>
        Object.entries(servicesStructure)
            .filter(([key, value]) => key && value.client_enabled)
            .map(([serviceKey, service]) => ({
                value: serviceKey,
                label: service.label,
            }));

    const getRoleOptions = (service: TServiceKey) => {
        const serviceStructure = servicesStructure[service];
        const roleFilterName = serviceStructure.role_filter_name;
        const roles = serviceStructure[roleFilterName];
        return Object.entries(roles)
            .filter(([key, value]) => key && value.client_enabled)
            .map(([key, value]) => ({
                value: key,
                label: value.label,
            }));
    };

    const getSkillOptions = (service: TServiceKey, role: string) => {
        const serviceStructure = servicesStructure[service];
        const roleFilterName = serviceStructure.role_filter_name;
        const skills = serviceStructure[roleFilterName][role];
        return Object.entries(skills)
            .filter(([key]) => key && !['label', 'icon_name', 'client_enabled'].includes(key))
            .map(([key, value]: [string, any]) => ({
                value: key,
                label: value.true.label,
            }));
    };

    useEffect(() => {
        fetchPayGrades();
        setServiceOptions(getServiceOptions());
    }, []);

    useEffect(() => {
        const roles = addFields.service
            ? getRoleOptions(addFields.service)
            : [];
        setRoleOptions(roles);
    }, [addFields.service]);

    useEffect(() => {
        const skills = addFields.service && addFields.role
            ? getSkillOptions(addFields.service, addFields.role)
            : [];
        setSkillOptions(skills);
    }, [addFields.role]);

    const getRoleStructure = (paygrade: TClientPayGrade) => {
        const serviceStructure = servicesStructure[paygrade.service];
        const roleFieldName = serviceStructure.role_field_name;
        const roleFilterName = serviceStructure.role_filter_name;
        return serviceStructure[roleFilterName][paygrade[roleFieldName]];
    };

    const onEditPayGradeClick = (paygrade: TClientPayGrade) => {
        setEditFields({
            client_pay_per_hour: paygrade.client_pay_per_hour,
            freelancer_pay_per_hour: paygrade.freelancer_pay_per_hour,
        });
        setEditingId(paygrade.id);
        setEditingError(null);
    };

    const clearAddFields = () => {
        setAddFields(initialAddFields);
    };

    const handleAddPayGrade = () => {
        // setIsAdding(true);
        setEditingError(null);

        const payload = {
            service: addFields.service,
            [servicesStructure[addFields.service].role_field_name]: addFields.role,
            freelancer_pay_per_hour: addFields.freelancer_pay_per_hour,
            client_pay_per_hour: addFields.client_pay_per_hour,
            ...addFields.skill ? { [addFields.skill]: true } : {},
        };

        executeHttpRequest(`clients/${clientId}/paygrades/`, 'post', payload)
            .then(() => {
                setIsAdding(false);
                clearAddFields();
                notification.success('Success', 'Pay grade added successfully');
                // Update the list of pay grades
                fetchPayGrades();
            })
            .catch(err => {
                setIsAdding(false);
                setEditingError(getErrorMessages(err));
            });
    };

    const handleEditPayGrade = (paygrade: TClientPayGrade) => {
        setIsEditing(true);
        setEditingError(null);

        const payload = {
            service: paygrade.service,
            ...editFields,
        };
        executeHttpRequest(
            `clients/${clientId}/paygrades/${paygrade.id}/`,
            'patch',
            payload,
        )
            .then(() => {
                setIsEditing(false);
                setEditingId(null);
                notification.success('Success', 'Pay grade updated successfully');
                // Update the list of pay grades
                fetchPayGrades();
            })
            .catch(err => {
                setIsEditing(false);
                setEditingError(getErrorMessages(err));
            });
    };

    const handleDeletePayGrade = () => {
        setIsDeleting(true);
        setDeletingError(null);

        const payload = { service: deletingPayGrade.service };
        executeHttpRequest(
            `clients/${clientId}/paygrades/${deletingPayGrade.id}/`,
            'delete',
            payload,
        )
            .then(() => {
                setIsDeleting(false);
                setDeletingPayGrade(null);
                notification.success('Success', 'Pay grade deleted successfully');
                // Update the list of pay grades
                fetchPayGrades();
            })
            .catch(err => {
                setIsDeleting(false);
                setDeletingError(getErrorMessages(err));
            });
    };

    const renderActionButtons = (paygrade: TClientPayGrade) =>
        editingId === paygrade.id
            ? (
                <td className="actions center">
                    <button
                        disabled={isEditing}
                        onClick={() => handleEditPayGrade(paygrade)}
                        type="button"
                    >
                        <i className="icon icon-check-12" />
                    </button>
                    <button
                        disabled={isEditing}
                        onClick={() => {
                            setEditingId(null);
                            setEditingError(null);
                        }}
                        type="button"
                    >
                        <i className="icon icon-close2" />
                    </button>
                </td>
            ) : (
                <td className="actions center">
                    <button
                        disabled={editingId && editingId !== paygrade.id}
                        onClick={() => onEditPayGradeClick(paygrade)}
                        type="button"
                    >
                        <i className="icon icon-pencil-3" />
                    </button>
                    <button
                        disabled={editingId && editingId !== paygrade.id}
                        onClick={() => {
                            setDeletingPayGrade(paygrade);
                            setDeletingError(null);
                        }}
                        type="button"
                    >
                        <i className="icon icon-bin-1" />
                    </button>
                </td>
        );

    const getSkillDisplay = (paygrade: TClientPayGrade) => {
        const roleStructure = getRoleStructure(paygrade);
        // @ts-ignore
        const selectedSkills = Object.keys(roleStructure)
            // @ts-ignore
            .filter(key => key && paygrade[key]);
        return selectedSkills
            .map(skill => roleStructure[skill].true.label)
            .join(', ');
    };

    const renderNumberInputField = (
        fieldName: 'client_pay_per_hour' | 'freelancer_pay_per_hour',
        fields: TAddPayGradeFields | TEditPayGradeFields,
        setFields: (_fields: any) => void,
        placeholder = '',
    ) => (
        <input
            name={fieldName}
            type="number"
            step="0.25"
            min="0"
            value={fields[fieldName]}
            onChange={event => {
                setFields({
                    ...fields,
                    [fieldName]: event.target.value,
                });
            }}
            placeholder={placeholder}
        />
    );

    const renderRows = () => payGrades.map((paygrade: TClientPayGrade) => (
        <tr key={paygrade.id}>
            <td>{servicesStructure[paygrade.service].label}</td>
            <td>{getRoleStructure(paygrade).label}</td>
            <td>{getSkillDisplay(paygrade)}</td>
            {editingId === paygrade.id
                ? (
                    <td className="client-paygrades__number-input">
                        {renderNumberInputField('freelancer_pay_per_hour', editFields, setEditFields)}
                    </td>
                ) : <td>£{paygrade.freelancer_pay_per_hour.toFixed(2)} /h</td>
            }
            {editingId === paygrade.id
                ? (
                    <td className="client-paygrades__number-input">
                        {renderNumberInputField('client_pay_per_hour', editFields, setEditFields)}
                    </td>
                ) : <td>£{paygrade.client_pay_per_hour.toFixed(2)} /h</td>
            }
            {renderActionButtons(paygrade)}
        </tr>
    ));

    const renderAddPayGradeRow = () => (
        <tr>
            <td>
                <Select
                    options={serviceOptions}
                    // @ts-ignore
                    value={addFields.service}
                    onChange={option => {
                        if (option.value !== addFields.service) {
                            setAddFields({
                                ...addFields,
                                service: option.value,
                                role: null,
                                skill: null,
                            });
                        }
                    }}
                    placeholder="Select service..."
                    clearable={false}
                />
            </td>
            <td>
                <Select
                    options={roleOptions}
                    // @ts-ignore
                    value={addFields.role}
                    disabled={!addFields.service}
                    onChange={option => {
                        if (option.value !== addFields.role) {
                            setAddFields({
                                ...addFields,
                                role: option.value,
                                skill: null,
                            });
                        }
                    }}
                    placeholder="Select role/vehicle..."
                    clearable={false}
                />
            </td>
            <td>
                <Select
                    options={skillOptions}
                    // @ts-ignore
                    value={addFields.skill}
                    disabled={!addFields.service || !addFields.role}
                    onChange={option => setAddFields({
                        ...addFields,
                        skill: option && option.value,
                    })}
                    placeholder="Select skill..."
                />
            </td>
            <td className="client-paygrades__number-input">
                {renderNumberInputField('freelancer_pay_per_hour', addFields, setAddFields)}
            </td>
            <td className="client-paygrades__number-input">
                {renderNumberInputField('client_pay_per_hour', addFields, setAddFields)}
            </td>
            <td className="actions center">
                <button
                    disabled={isAdding || !addFields.service || !addFields.role || 
                        !addFields.client_pay_per_hour || !addFields.freelancer_pay_per_hour}
                    onClick={handleAddPayGrade}
                    type="button"
                >
                    <i className="icon icon-add-12" />
                </button>
                <button
                    disabled={isAdding || !Object.values(addFields).some(field => field)}
                    onClick={clearAddFields}
                    type="button"
                >
                    <i className="icon icon-close2" />
                </button>
            </td>
        </tr>
    );

    const renderPayGradeDetails = (paygrade: TClientPayGrade) => (
        <div style={{ marginTop: '20px', textAlign: 'left' }}>
            <p><strong>Service:</strong> {servicesStructure[paygrade.service].label}</p>
            <p><strong>Role/vehicle:</strong> {getRoleStructure(paygrade).label}</p>
            <p><strong>Skill:</strong> {getSkillDisplay(paygrade) || 'None'}</p>
            <p><strong>Pay rate:</strong> £{paygrade.freelancer_pay_per_hour.toFixed(2)} /h</p>
            <p><strong>Charge rate:</strong> £{paygrade.client_pay_per_hour.toFixed(2)} /h</p>
        </div>
    );

    return (
        <div className="freelancer-details-section">
            <div className="freelancer-details-section__header">
                <div className="freelancer-details-section__title">Pay grades</div>
            </div>
            <div className="freelancer-details-section__content">
                {!!editingError && (
                    <div className="freelancer-details-section__errors">
                        <p data-testid="errorMsg">{editingError}</p>
                    </div>
                )}
                <table className="table-default table-layout-fixed">
                    <tbody>
                        <tr>
                            <td className="table-default__cell--text-blue">Service</td>
                            <td className="table-default__cell--text-blue">Role/Vehicle</td>
                            <td className="table-default__cell--text-blue">Skill</td>
                            <td className="table-default__cell--text-blue sm-col">Pay rate</td>
                            <td className="table-default__cell--text-blue sm-col">Charge rate</td>
                            <td className="table-default__cell--text-blue center table-default__cell--action">
                                Action
                            </td>
                        </tr>
                        {renderRows()}
                        {renderAddPayGradeRow()}
                    </tbody>
                </table>
            </div>
            {!!deletingPayGrade && (
                <ModalConfirmationWithErrorAndLoader
                    className={null}
                    errorMessage={deletingError}
                    handleCancel={() => {
                        setDeletingPayGrade(null);
                        setDeletingError(null);
                    }}
                    handleContinue={handleDeletePayGrade}
                    isError={!!deletingError}
                    isInProgress={isDeleting}
                    setIsInProgress={setIsDeleting}
                    isOpen={!!deletingPayGrade}
                    title="Delete Location"
                >
                    <p className="modal-title">Delete Pay Grade</p>
                    <p>Are you sure you want to delete this pay grade?</p>
                    {renderPayGradeDetails(deletingPayGrade)}
                </ModalConfirmationWithErrorAndLoader>
            )}
        </div>
    );
}
